import React, {
  useCallback, useState, useMemo, ReactElement,
} from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useParams, Navigate } from 'react-router';
import FlexContainer from '../../../../components/flex-container/flex-container.component';
import PaddedFlexContainer from '../../../../components/padded-flex-container/padded-flex-container.component';
import Grid from '../../../../components/grid/grid.component';
import GridItem from '../../../../components/grid-item/grid-item.component';
import Button from '../../../../components/button/button.component';
import FieldText from '../../../../components/field-text/field-text.component';
import TextHeader from '../../../../components/text-header/text-header.component';
import TextBlock from '../../../../components/text-block/text-block.component';
import ErrorBlock from '../../../../components/error-block/error-block.component';
import Form from '../../../../components/form/form.component';
import FormContainer from '../../../../components/form-container/form-container.component';
import FormFieldPassword from '../../../../components/form-field-password/form-field-password.component';
import PasswordRecoveryValidator from './password-recovery.validator';
import FormService from '../../../../services/form-service';
import useFormStructureModified from '../../../../hooks/use-form-structure-modified';
import useScreenMobile from '../../../../hooks/use-screen-mobile';
import useServerFormErrors from '../../../../hooks/use-server-form-errors';
import { changePassword } from './password-recovery.action';
import { resetFormErrors } from '../../app.action';
import { FORM_ID_PASSWORD_RECOVERY } from '../../../../constants/system/form-ids';
import {
  ROUTE_DASHBOARD, ROUTE_LOGIN, ROUTE_NOT_FOUND, ROUTE_VERIFICATION_SETUP, ROUTE_PARAM_HASH,
} from '../../../../constants/system/routes';
import { ACTION_TYPE_VERIFICATION_SETUP } from '../../../../constants/entity/action-types';

const PasswordRecovery: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isPasswordRecovered,
    currentUser,
    actionHash,
    actionType,
    homepageRoute,
    form: formProps,
    modified: formModifiedFields,
    handleSubmit: submitForm,
    pristine: isFormPristine,
    isSubmitting: isFormSaving,
    error: formError,
    values: formValues,
    hasValidationErrors: withFormErrors,
    formErrors: serverFormErrors,
    mainFormError: mainServerFormError,
  } = props;

  const routeParams: ReactRouterParams = useParams();
  const isMobileScreen: boolean = useScreenMobile();
  const isFormStructureModified: boolean = useFormStructureModified(formModifiedFields, true);
  const dispatch: ReduxDispatch = useDispatch();

  const ACTION_TYPE_ROUTE_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [ACTION_TYPE_VERIFICATION_SETUP]: ROUTE_VERIFICATION_SETUP,
  }), []);

  const actionHashParam: string = routeParams[ROUTE_PARAM_HASH];
  const isSaveDisabled: boolean = isFormSaving || (isFormPristine && !isFormStructureModified);
  const actionTypeRoute: string = actionType && ACTION_TYPE_ROUTE_MAP[actionType];
  const withServerErrors: boolean = !!serverFormErrors && !!Object.keys(serverFormErrors).length;

  const [isPasswordVisible, setIsPasswordVisible]: [boolean, Function] = useState(false);
  const [isPasswordConfirmVisible, setIsPasswordConfirmVisible]: [boolean, Function] = useState(false);

  const sendForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(changePassword({
        changePasswordForm: {
          password: values.password,
          actionHash: actionHashParam,
        },
      }, (response: ActionResponse): void => {
        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, [actionHashParam]);

  const onSubmitForm: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendForm(formValues);
    } else {
      FormService.focusOnFirstFormError();
    }
  }, [actionHashParam, withFormErrors, formValues]);

  const onPasswordVisibilityChange: Function = useCallback((value: boolean): void => {
    setIsPasswordVisible(value);
  }, []);

  const onPasswordConfirmVisibilityChange: Function = useCallback((value: boolean): void => {
    setIsPasswordConfirmVisible(value);
  }, []);

  const onFormChange: Function = useCallback((event: React.SyntheticEvent): void => {
    const fieldName: string = event.target && (event.target as HTMLInputElement).name;

    if (withServerErrors && fieldName) {
      dispatch(resetFormErrors());
      FormService.resetFormServerErrors(formProps, fieldName);
      submitForm();
    }
  }, [withServerErrors, formProps]);

  useServerFormErrors(serverFormErrors, (): void => {
    submitForm();
  });

  if (!actionHashParam) {
    return <Navigate to={ROUTE_NOT_FOUND} />;
  }

  if (isPasswordRecovered) {
    return <Navigate to={ROUTE_LOGIN} />;
  }

  if (currentUser && currentUser.id) {
    return <Navigate to={homepageRoute || ROUTE_DASHBOARD} />;
  }

  if (actionHash && actionTypeRoute) {
    return <Navigate to={`${actionTypeRoute}/${actionHash}`} />;
  }

  return (
    <Form withAutocomplete id={FORM_ID_PASSWORD_RECOVERY} onChange={onFormChange}>
      <FlexContainer isCentered>
        <PaddedFlexContainer isLarge isColumn isTextCenter width={900}>
          <Grid withLargeSpacing>
            <GridItem isFullWidth>
              <TextHeader>
                Password Recovery
              </TextHeader>
              <TextBlock>
                Please enter new password
              </TextBlock>
            </GridItem>
            <GridItem isFullWidth={isMobileScreen} isMediumWidth={!isMobileScreen}>
              <FieldText isPlain isHidden withAutocompleteUserName name="email" value="" />
              <FormFieldPassword
                isOutlined
                withStrengthBar
                withAutocompleteNewPassword
                isVisible={isPasswordVisible}
                name="password"
                label="Password"
                onVisibilityChange={onPasswordVisibilityChange}
              />
            </GridItem>
            <GridItem isFullWidth={isMobileScreen} isMediumWidth={!isMobileScreen}>
              <FormFieldPassword
                isOutlined
                withNoAutocomplete
                isVisible={isPasswordConfirmVisible}
                name="passwordConfirm"
                label="Confirm password"
                onVisibilityChange={onPasswordConfirmVisibilityChange}
              />
            </GridItem>
            <GridItem isFullWidth>
              <Button
                isActive
                isDefault
                isExtended
                isDisabled={isSaveDisabled}
                onClick={onSubmitForm}
              >
                Change Password
              </Button>
            </GridItem>
          </Grid>
          {
            (!!formError || !!mainServerFormError) && !isFormSaving && (
              <ErrorBlock errorMessage={formError || mainServerFormError} />
            )
          }
        </PaddedFlexContainer>
      </FlexContainer>
    </Form>
  );
});

const initialValues: FormValues = {};
const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
    passwordRecovery,
  } = reducers;

  initialValues.password = null;
  initialValues.passwordConfirm = null;

  return {
    currentUser: app.baseData && app.baseData.currentUser,
    isPasswordRecovered: passwordRecovery.isPasswordRecovered,
    actionHash: passwordRecovery.actionHash,
    actionType: passwordRecovery.actionType,
    homepageRoute: passwordRecovery.homepageRoute,
  };
};

const PasswordRecoveryForm: ReactComponent = (props: ReactComponentProps): ReactElement => {
  const formErrors: ValidationErrors = useSelector(({ passwordRecovery }: Reducers): ValidationErrorsMap => (
    passwordRecovery.formErrors
  ));
  const isFormSaving: ValidationErrors = useSelector(({ passwordRecovery }: Reducers): ValidationErrorsMap => (
    passwordRecovery.isFormSaving
  ));

  return (
    <FormContainer
      formId={FORM_ID_PASSWORD_RECOVERY}
      component={PasswordRecovery}
      validator={PasswordRecoveryValidator}
      initialValues={initialValues}
      formErrors={formErrors}
      isSubmitting={isFormSaving}
      {...props}
    />
  );
};

export default connect(mapState)(PasswordRecoveryForm);
